import { useState, useEffect } from 'react';
import Select from '@mui/joy/Select';
import Grid from '@mui/joy/Grid';
import Option from '@mui/joy/Option';
import LanguageIcon from '@mui/icons-material/Language';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import MicIcon from '@mui/icons-material/Mic';
import Tooltip from '../common/Tooltip';
import SelectGroupedOptions from '../common/GroupSelect';

const buildVoiceOptions = (handler, language, voices) => {
  const byHandler = handler && voices && typeof voices === 'object' && voices[handler];
  if (!byHandler || typeof byHandler !== 'object') return undefined;
  return Object.fromEntries(
    Object.entries(byHandler).map(
      ([provider, languages]) =>
        ([provider,
          languages?.[language]?.map(voice => ({ ...voice, description: `${voice.description} (${voice.gender})` })),
        ]),
    ),
  );
};

// Is the voice one of the currently available voices?
const haveVoice = (voiceOptions, voice) => voiceOptions && voice && Object.values(voiceOptions).some(a => Array.isArray(a) && a.some(v => v?.name === voice));

export default function SelectVoice({
  disabled,
  language,
  modelName,
  setLanguage,
  defaultLanguage,
  voice,
  setVoice,
  voices,
  /** When set (e.g. from GET /models/{modelName}/voices), drives the language list instead of inferring from `voices`. */
  localeChoices,
  /** STT provider options from GET /models/{modelName}/recognition/{locale}. When non-empty, shows STT picker. */
  sttProviders,
  /** Currently selected STT vendor name. */
  sttVendor,
  /** Setter for STT vendor. */
  setSttVendor,
  tooltip
}) {
  let [languages, setLanguages] = useState();
  let [voiceOptions, setVoiceOptions] = useState();
  let [handler] = modelName?.split(':') || [];

  // No model selected — clear per-model lists. (Otherwise stale `languages` + empty voice options
  // causes setLanguage(null) / repick loops after e.g. deleting an agent and clearing modelName.)
  useEffect(() => {
    if (!handler) {
      setLanguages(undefined);
      setVoiceOptions(undefined);
    }
  }, [handler]);

  useEffect(() => {
    if (localeChoices?.length && language != null && !localeChoices.includes(language)) {
      setLanguage(null);
    }
  }, [localeChoices, language, setLanguage]);

  useEffect(() => {
    if (language && voices) {
      if (!handler) {
        return;
      }
      let options = buildVoiceOptions(handler, language, voices) || {};
      if (Object.keys(options).length) {
        setVoiceOptions(options);
      }
      else if (!localeChoices?.length) {
        // Without per-model locales, empty options means bad language. With `localeChoices`,
        // voices are filled async after language is chosen — do not clear language while loading.
        setLanguage(null);
      }
    }
    if (localeChoices?.length) {
      setLanguages([...localeChoices].sort((a, b) => a.localeCompare(b)));
      return;
    }
    handler && voices?.[handler] && setLanguages(Object.keys(Object.values(voices[handler]).reduce((o, v) => ({ ...o, ...v }), {})));
  }, [language, setLanguage, voices, handler, defaultLanguage, localeChoices]);

  useEffect(() => {
    if (languages?.length && !language) {
      setLanguage(languages.find(l => l === defaultLanguage) || languages[0]);
    }
  }, [languages, language, defaultLanguage, setLanguage]);

  useEffect(() => {

    // voiceOptions can change asynchronously at any time as they can be a function of the selected agent 
    // This effect actions the case where either an initial voice has not been set, or there is a voice but
    // it is invalid because it is not available when using the currently selected agent.
    let [, voiceName] = voice?.split(':') || [0, voice];
    if (voiceOptions && Object.keys(voiceOptions).length && (!voice || !haveVoice(voiceOptions, voiceName))) {
      let [provider, pVoices] = Object.entries(voiceOptions)?.[0] || []
      let def = pVoices?.find(l => l === defaultLanguage)?.name;
      let first = pVoices?.[0]?.name;
      let voiceString = provider && (def || first) && `${provider}:${def || first}`
      setVoice(voiceString);
 
    }
  }, [voice, voices, handler, voiceOptions, defaultLanguage, setVoice]);

  const hasStt = Array.isArray(sttProviders) && sttProviders.length > 0;

  return (

    <Grid container sx={{ width: '100%' }}>
      <Grid xs={12} sm={4}>
        <Select
          value={language}
          disabled={disabled}
          placeholder="select language"
          label="language"
          startDecorator={<LanguageIcon />}
          onChange={(e, value) => setLanguage(value)} sx={{ m: 2 }}>
          {languages?.length && languages.map(option => (
            <Option key={option} value={option}>{`${option}`}</Option>
          ))}
        </Select>
      </Grid>
      {hasStt && (
        <Grid xs={12} sm={4}>
          <Select
            value={sttVendor ?? null}
            disabled={disabled}
            placeholder="select STT"
            startDecorator={<MicIcon />}
            onChange={(e, value) => setSttVendor && setSttVendor(value)}
            sx={{ m: 2 }}
          >
            {sttProviders.map((p) => (
              <Option key={p.name} value={p.name}>{p.description}</Option>
            ))}
          </Select>
        </Grid>
      )}
      <Grid xs={12} sm={hasStt ? 4 : 8}>
        <Tooltip {...{ tooltip }} open={!!tooltip}>
          <SelectGroupedOptions
            value={voice}
            disabled={disabled}
            placeholder="select voice"
            startDecorator={<RecordVoiceOverIcon />}
            onChange={(e, value) => (setVoice(value))}
            sx={{ m: 2 }}
            options={voiceOptions}
          >
          </SelectGroupedOptions>
        </Tooltip>
      </Grid>
    </Grid>

  );

}